import { mocked } from '@/configs';

import { Constructor } from '@/type-utils';
import { RecommendationContext } from '@/configs/recommendations';

import MockService, { MockState, ServiceMock } from '../MockService';
import type { RecommendationService } from './RecommendationService';
import { Brand, EnvironmentService } from '../EnvironmentService';
import type IRecommendationsData from './IRecommendationsData';
import type { IStreamedRecommendationsData } from './IStreamedRecommendationsData';
import { getContextConfig } from './getContextConfig';
import { ProductListService } from '../ProductListService';

const AHNU_MOCK_RECOMMENDATION = {
  title: 'New Arrivals',
  parentTitle: '',
  productList: [
    '1166670-WWH',
    '1166670-WWH',
    '1166670-WWH',
    '1166670-WWH',
    '1166670-WWH',
    '1166670-WWH'
  ],
  slotIds: ['slot1', 'slot2', 'slot3', 'slot4', 'slot5', 'slot6']
};

const TEVA_MOCK_RECOMMENDATION = {
  title: 'You Might Also Like',
  parentTitle: '',
  productList: [
    '1090969-BBKL',
    '1090969-BASL',
    '1090969-CYM',
    '1090969-MCBM',
    '1090969-BLMC-09',
    '1019234-MTNM-13'
  ],
  slotIds: ['slot1', 'slot2', 'slot3', 'slot4', 'slot5', 'slot6']
};

const TEVA_MOCK_PLP_TRENDING = {
  title: 'Trending',
  parentTitle: 'Explore These Styles',
  productList: ['1090969-BLK'],
  slotIds: ['slot1']
};

const TEVA_MOCK_PLP_RECENTLY_VIEWED = {
  title: 'Recently Viewed',
  parentTitle: '',
  productList: ['1090969-BLK'],
  slotIds: ['slot1']
};

const TEVA_MOCK_PLP_FRANCHISE = {
  title: 'Franchise Collection',
  parentTitle: '',
  productList: ['1090969-BLK'],
  slotIds: ['slot1']
};

/**
 * Gets the mock recommendation data for the current brand.
 * @param context - The recommendation context.
 * @returns Mock recommendations data.
 */
export const MOCK_RECOMMENDATION = (
  context?: RecommendationContext
): IRecommendationsData => {
    const brand = ('AHNU');
  // Handle PLP-specific contexts for Teva
  if (brand === Brand.Teva && context !== undefined) {
    switch (context) {
      case RecommendationContext.PLPTrending: {
        return TEVA_MOCK_PLP_TRENDING;
      }
      case RecommendationContext.PLPRecentlyViewed: {
        return TEVA_MOCK_PLP_RECENTLY_VIEWED;
      }
      case RecommendationContext.PLPFranchise: {
        return TEVA_MOCK_PLP_FRANCHISE;
      }
      default: {
        // For all other contexts, fall through to default behavior
        break;
      }
    }
  }

  switch (brand) {
    case Brand.Ahnu: {
      return AHNU_MOCK_RECOMMENDATION;
    }
    case Brand.Teva: {
      return TEVA_MOCK_RECOMMENDATION;
    }
    default: {
      return AHNU_MOCK_RECOMMENDATION;
    }
  }
};

const initialState = {};

/**
 * Mock implementation of the RecommendationService class.
 */
class RecommendationServiceMock extends ServiceMock<RecommendationService> {
  protected _state;

  /** @inheritdoc */
  public get state(): MockState {
    return this._state;
  }

  /** @inheritdoc */
  public getMock(): RecommendationService {
    return MockService.getMockOf(
      this.service
    ) as unknown as RecommendationService;
  }

  /**
   * The constructor to initialize the mocks.
   * @param service - The service that is being mocked.
   */
  public constructor(private service: Constructor<RecommendationService>) {
    super();
    this._state = new MockState(initialState);
    this.initializeMockedMembers(service);
  }

  /** @inheritdoc */
  protected initializeMockedMembers(
    service: Constructor<RecommendationService>
  ): void {
    const mockEnabled: boolean = mocked.RecommendationService;

    MockService.mockService(
      mockEnabled,
      service,
      {
        getRecommendations: async (context: RecommendationContext) => {
          return Promise.resolve(MOCK_RECOMMENDATION(context));
        },
        getContextConfig: (context: RecommendationContext) => {
          const { isRecommendationEnabled, isSlideOnMobile } =
            getContextConfig(context);
          return { isRecommendationEnabled, isSlideOnMobile };
        },
        streamProductsRecommendations: async (
          context: RecommendationContext
        ): Promise<IStreamedRecommendationsData> => {
          const { isRecommendationEnabled } = getContextConfig(context);

          if (!isRecommendationEnabled) {
            return {
              title: null,
              parentTitle: null,
              gridItems: [],
              isRecommendationEnabled: false
            };
          }

          const { productList, title, parentTitle } =
            MOCK_RECOMMENDATION(context);

          if (productList.length === 0) {
            return {
              title,
              parentTitle,
              gridItems: [],
              isRecommendationEnabled: true
            };
          }

          const gridItems =
            await ProductListService.streamProductsFromSkus(productList);

          return {
            title,
            parentTitle,
            gridItems,
            isRecommendationEnabled: true
          };
        }
      },
      {},
      this.state
    );
  }
}

export default RecommendationServiceMock;
